import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import templatesApi from "api/landlord/templates";
import { Button, ButtonVariant, Modal, Show } from "shared/components";
import { VerificationNames } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";
import { IntroView } from "./RerunIncomeModal/slices/IntroView";
import { SuccessModal } from "./ReverifyIncomeModal/slices/components/SuccessModal";

interface Props {
  opened: boolean;
  onClose: () => void;
}

enum Views {
  Intro,
  RequestSent
}

export const RequestIncomeVerificationModal = ({ opened, onClose }: Props) => {
  const { applicant, requestNewIncome, isRequestingNewIncome } =
    useCombinedIncomeContext();
  const [view, setView] = useState<Views>(Views.Intro);

  useEffect(() => {
    if (opened) setView(Views.Intro);
  }, [opened]);

  const { data: schemas } = useQuery({
    queryKey: ["VERIFICATION_SCHEMAS"],
    queryFn: () => templatesApi.getVerificationSchemas()
  });

  const combinedIncomeSchema = schemas?.verification_schemas.find(
    (v) => v.name === VerificationNames.CombinedIncome
  );
  const priceCents = combinedIncomeSchema?.screening_fee.cents || 0;

  const applicantFullName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[488px] w-full flex flex-col"
    >
      <div className="flex-grow min-h-0">
        <Show if={view === Views.Intro}>
          <IntroView
            onClose={onClose}
            applicantFullName={applicantFullName}
            applicantRole={getApplicantRole(applicant.role)}
            email={applicant.email}
            phone={applicant.phone}
            cents={priceCents}
            isRerun={false}
            title="Request income verification"
            description="Boom will send an income verification request to the applicant via email and SMS. You'll get an email once the income data is received."
          />
        </Show>

        <Show if={view === Views.RequestSent}>
          <SuccessModal
            text="Request has been sent to the applicant"
            onClose={onClose}
          />
        </Show>
      </div>

      <Show if={view === Views.Intro}>
        <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end self-end w-full">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-[132px]"
            loading={isRequestingNewIncome}
            onClick={() =>
              requestNewIncome(undefined, {
                onSuccess: () => setView(Views.RequestSent)
              })
            }
          >
            Send request
          </Button>
        </div>
      </Show>
    </Modal>
  );
};
